import React from 'react';
import { useTranslation } from '../../i18n';
import { CategoryVote, AppIcon } from '../shared';
import { GameConfig } from '../../config/types';
import { gameHasCategories } from '../../config/categories';
import { TeamsState, TeamId } from '../../types/teams';
import { RoomCodeDisplay } from './RoomCodeDisplay';
import { SelectedGameCard } from './SelectedGameCard';
import { LobbyPlayersColumn } from './LobbyPlayersColumn';

interface LobbyPlayer {
  id: string;
  name: string;
  score: number;
  avatar?: string;
  teamId?: 'A' | 'B';
}

interface PlayerLobbyProps {
  roomName: string;
  games: GameConfig[];
  currentGameType?: string;
  categoryVotes: Record<string, number>;
  myCategoryVotes: string[];
  players: LobbyPlayer[];
  teams?: TeamsState;
  showTeamAssign: boolean;
  myPlayerId?: string;
  hasVotedBefore: boolean;
  brandingDisplayName?: string;
  brandingLogoUrl?: string;
  onSetTeam: (playerId: string, teamId: TeamId | null) => void;
  onCategoryVote: (category: string) => void;
}

export const PlayerLobby: React.FC<PlayerLobbyProps> = ({
  roomName,
  games,
  currentGameType,
  categoryVotes,
  myCategoryVotes,
  players,
  teams,
  showTeamAssign,
  myPlayerId,
  hasVotedBefore,
  brandingDisplayName,
  brandingLogoUrl,
  onSetTeam,
  onCategoryVote,
}) => {
  const { t } = useTranslation();
  const selectedGame = currentGameType
    ? games.find((g) => g.id === currentGameType)
    : undefined;

  return (
    <div className="lobby-shell lobby-shell--player">
      <div className="host-main-layout lobby-top-row">
        <div className="host-qr-column">
          {(brandingLogoUrl || brandingDisplayName) && (
            <div className="host-qr-branding" data-testid="room-branding">
              {brandingLogoUrl && (
                <img src={brandingLogoUrl} alt="" className="host-qr-branding__logo" />
              )}
              {brandingDisplayName && (
                <div className="host-qr-branding__name">{brandingDisplayName}</div>
              )}
            </div>
          )}
          <div className="host-qr-room-code-top">
            <div className="host-qr-room-code-label-row">
              <span>{t('lobby.gameSelection.roomCodeLabel')}</span>
              <RoomCodeDisplay code={roomName} />
            </div>
          </div>
          <div className="waiting-message" style={{ marginTop: '0.5rem', marginBottom: 0 }}>
            <p>
              <AppIcon name="hourglass" fontSize="small" />{' '}
              {t('lobby.gameSelection.waitingForHost')}
            </p>
          </div>
          {selectedGame && (
            <SelectedGameCard
              gameId={selectedGame.id}
              name={selectedGame.name}
              extendedDescription={selectedGame.extendedDescription}
              className="player-selected-game-below-waiting"
            />
          )}
        </div>

        <LobbyPlayersColumn
          players={players}
          teams={teams}
          showTeamAssign={showTeamAssign}
          canAssignAny={false}
          myPlayerId={myPlayerId}
          onSetTeam={onSetTeam}
        />

        <div className="host-right-column">
          {selectedGame && (
            <SelectedGameCard
              gameId={selectedGame.id}
              name={selectedGame.name}
              extendedDescription={selectedGame.extendedDescription}
              className="player-selected-game-in-column player-selected-game-desktop-only"
            />
          )}
          {currentGameType && gameHasCategories(currentGameType) && (
            <CategoryVote
              gameType={currentGameType}
              myCategoryVotes={myCategoryVotes}
              categoryVotes={categoryVotes}
              onCategoryVote={onCategoryVote}
              maxVotes={3}
              disabled={hasVotedBefore}
            />
          )}
        </div>
      </div>
    </div>
  );
};
